import { Container } from '@amzn/awsui-components-react';
import { useEffect, useMemo } from 'react';
import { fetchDomainInsights, useGetInsightsQuery } from './Hooks/useGetInsightsQuery';
import { useClusterHealthSummaryQuery } from './Hooks/useClusterHealthSummaryQuery';
import { useDomainIdentuple } from './Hooks/useDomainIdentuple';
import { InsightsTable as InsightsTables } from './Components/InsightsTable';
import { ClusterHealthSummary } from './Components/ClusterHealthSummary';
import { ClusterMetrics } from './Components/ClusterMetrics';
import { AnyObject, CommonDomainProps } from '../../models/types';
import { InsightsTable } from '@amzn/search-services-cluster-insights-commons-lib';
import { useToastNotifications } from '../../hooks/useToastList';
import { useApolloClient } from '@apollo/client';
import { fetchDomainInsightDetails } from './Hooks/useGetInsightDetailsQuery';
import { useDomainConfigQuery } from '../DomainConfig/hooks/useDomainConfigQuery';
import get from 'lodash/get';

interface ClusterInsightProps extends CommonDomainProps {
  domainIdentifier: string;
}

const ClusterInsights = (props: ClusterInsightProps) => {
  const client = useApolloClient();
  const notifications = { toasts: useToastNotifications() }
  const { 
    loading: getClusterHealthSummaryLoading,
    data: getClusterHealthSummaryData
  } = useClusterHealthSummaryQuery({
    domainIdentifier: props.domainIdentifier,
  });
  const domainIdentuple = useDomainIdentuple(props.domainIdentifier);
  const fetchDomainInsightsHandler = async (_http, domainIdent, dateRange) => await fetchDomainInsights(client, domainIdentuple, dateRange);
  const fetchInsightDetailsForDomainHandler = async (_http, indightID, domainIdent) => await fetchDomainInsightDetails(client, domainIdentuple, indightID);
  const { loading, data, error } = useDomainConfigQuery({
    domainIdentifier: props.domainIdentifier,
  });
  const bestPracticesPanelParameters = {
    ClusterConfig: {
      DedicatedMasterEnabled: get(data, 'domain.rawConfig.elasticsearchClusterConfig.elasticsearchClusterConfig.dedicatedMasterEnabled', {}),
      MultiAZWithStandbyEnabled: get(data, 'domain.rawConfig.elasticsearchClusterConfig.elasticsearchClusterConfig.multiAzWithStandbyEnabled', {})
    },
    AutoTuneOptions: {
      State: get(data, 'domain.rawConfig.autoTuneOptions.status.state', {})
    },
    EncryptionAtRestOptions: {
      Enabled: get(data, 'domain.rawConfig.encryptionAtRestOptions.encryptionAtRestOptions.enabled', {})
    },
    NodeToNodeEncryptionOptions: {
      Enabled: get(data, 'domain.rawConfig.nodeToNodeEncryptionOptions.nodeToNodeEncryptionOptions.enabled', {})
    },
    AdvancedSecurityOptions: {
      Enabled: get(data, 'domain.rawConfig.advancedSecurityOptions.advancedSecurityOptions.enabled', {})
    },
  }

  // Pick latest 1 hour window with 1 min period when user lands on page (Mimicks OpenSearch console behaviour)
  const initialTimeRange = useMemo(() => {
    const end = Math.floor(Date.now());
    return {
      endTime: end,
      startTime: end - (60 * 60 * 1000),
      period: 1 // 1 min
    };
  }, []);

  return (
    <>
      <Container
        header={
          <div>
            <ClusterHealthSummary 
              getClusterHealthSummaryData={getClusterHealthSummaryData}
              getClusterHealthSummaryLoading={getClusterHealthSummaryLoading}
              domainIdent={domainIdentuple}
              bestPracticesPanelParameters={bestPracticesPanelParameters}
              domainData={data}
            />
            <br />
            {domainIdentuple && <InsightsTable
              http={{}}
              notifications={notifications}
              fetchInsightsForDomain={fetchDomainInsightsHandler}
              fetchInsightDetailsForDomain={fetchInsightDetailsForDomainHandler}
              domainIdent={domainIdentuple}
            />}
            <br />
            {domainIdentuple && ( 
              <ClusterMetrics
                domainIdentuple={domainIdentuple}
                startTime={initialTimeRange.startTime}
                endTime={initialTimeRange.endTime}
                period={initialTimeRange.period}
              />
            )}
          </div>
        }
      >
      </Container>
    </>
  );
};

export { ClusterInsights };
